import React from 'react';
import { ContentAlign, TextColor, TitleSize } from '@api/selections';
import { BlockComponentBaseProps, EpiImage, ImageConfig } from '@api/types';
import { BlockContainer, Button, EditPanel, Html, Image, SmartLink } from '@components';
import { Title } from '@components/common/Text/Title/Title';
import { Box, Stack, styled, useMediaQuery, useTheme } from '@mui/material';
import { breakpoints, palette } from '@themes/theme.helpers';
import { ImageResizerParams } from '@utils/images.utils';

// STYLES
//--------
const ImageBlockWrapper = styled(Box)<ImageBlockProps>`
  color: ${palette.primary('main')};
  position: relative;
  .epi-image {
    max-width: 100%;
    height: auto;
  }
`;

const ContentWrapper = styled(Box)<ImageBlockProps>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: ${(p) => {
    switch (p.textAlign) {
      case 'left':
        return 'flex-start';
      case 'right':
        return 'flex-end';
      default:
        return 'center';
    }
  }};
  padding: 20px;
  ${breakpoints.up('md')} {
    padding: 40px;
  }
`;

// TYPES
//-------

type CtaButton = {
  buttonText?: string;
  link?: string;
};

export type ImageBlockProps = BlockComponentBaseProps & {
  children?: React.ReactNode;
  title?: string;
  titleSize?: TitleSize;
  textAlign?: ContentAlign;
  titleColor?: TextColor;
  url?: string;
  image?: EpiImage;
  imageConfig?: ImageConfig;
  text?: string;
  ctaButton1?: CtaButton;
  ctaButton2?: CtaButton;
};

// COMPONENTS
//------------
export const ImageBlock = ({
  title,
  titleSize,
  titleColor,
  textAlign,
  image,
  url,
  text,
  ctaButton1,
  ctaButton2,
  imageConfig,
  disableEditPanel = false,
}: ImageBlockProps) => {
  let resizerParams: ImageResizerParams = { format: 'webp', width: 1200 };
  const theme = useTheme();
  const isBreakpointMedium = useMediaQuery(theme.breakpoints.up('md'));

  const content = (
    <ImageBlockWrapper className='image-block'>
      <EditPanel
        disableEditPanel={disableEditPanel}
        properties={[
          'title',
          'titleColor',
          'titleSize',
          'text',
          'ctaButton1',
          'ctaButton2',
          'textAlign',
          'image',
          'imageConfig',
          'url',
        ]}
      />
      <Image image={image} {...imageConfig} resizerParams={resizerParams} />
      <ContentWrapper textAlign={textAlign} className='fade-in-transition fast'>
        <Title variant={titleSize} color={titleColor}>
          {title}
        </Title>
        <Html
          html={text}
          sx={{
            '& p': {
              margin: 0,
            },
          }}
        />
        {!url && (ctaButton1?.buttonText || ctaButton2?.buttonText) && (
          <Stack
            spacing={2}
            direction={isBreakpointMedium ? 'row' : 'column'}
            mt={isBreakpointMedium ? 4 : 2}
            flexWrap={'wrap'}
          >
            {ctaButton1?.buttonText && (
              <Button
                variant={isBreakpointMedium ? 'primary' : 'secondary'}
                href={ctaButton1?.link}
              >
                {ctaButton1?.buttonText}
              </Button>
            )}
            {ctaButton2?.buttonText && (
              <Button
                variant={isBreakpointMedium ? 'primary' : 'secondary'}
                href={ctaButton2?.link}
              >
                {ctaButton2?.buttonText}
              </Button>
            )}
          </Stack>
        )}
      </ContentWrapper>
    </ImageBlockWrapper>
  );
  return url ? (
    <BlockContainer fullscreen>
      <SmartLink href={url} disableInEdit>
        {content}
      </SmartLink>
    </BlockContainer>
  ) : (
    <BlockContainer fullscreen>{content}</BlockContainer>
  );
};
